import React, { useState, useEffect } from "react";
import axiosInstance from "../services/api/axios";
import { searchCategories } from "../services/materialAdmin";
import { toast } from "react-toastify";
import {
  CloseButton,
  DeleteButton,
  EditButton,
  PrimaryButton,
} from "./Buttons";
import ConfirmationModal from "./ConfirmationModal";
import CustomInput from "./CustomInput";

/**
 * Reusable Developer Project BOQ table with full CRUD (per phase).
 * APIs:
 *   GET  /api/interior-designers/developer-project-boq?projectPhaseId=
 *   POST /api/interior-designers/developer-project-boq (body: { projectPhaseId, items: [{ materialCategoryId, details }] })
 *   PUT  /api/interior-designers/developer-project-boq/{boq_id} (body: { materialCategoryId, details })
 *   DELETE /api/interior-designers/developer-project-boq/{boq_id}
 *
 * @param {Object} props
 * @param {string|number} props.projectPhaseId - Project phase ID (required)
 * @param {string} [props.title] - Optional title above the table (default: "Developer Project BOQ")
 * @param {boolean} [props.showTitle=true] - Whether to show the title
 * @param {string} [props.className] - Optional class for the wrapper div
 * @param {boolean} [props.readOnly=false] - Hide CRUD; show material category + details only (e.g. BV viewer)
 */
function DeveloperProjectBoqTable({
  projectPhaseId,
  title = "Developer Project BOQ",
  showTitle = true,
  className = "",
  readOnly = false,
}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [newItems, setNewItems] = useState([]);
  const [savingRowIndex, setSavingRowIndex] = useState(null);

  const [editingItem, setEditingItem] = useState(null);
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editDetails, setEditDetails] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [boqToDeleteId, setBoqToDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const phaseId = projectPhaseId != null ? parseInt(projectPhaseId, 10) : null;

  const fetchList = async () => {
    if (phaseId == null || isNaN(phaseId)) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        "/interior-designers/developer-project-boq",
        { params: { projectPhaseId: phaseId } }
      );
      setList(Array.isArray(response) ? response : []);
      setError("");
    } catch (err) {
      console.error("Error fetching developer project BOQ:", err);
      setError("Failed to fetch developer project BOQ.");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [projectPhaseId]);

  useEffect(() => {
    if (readOnly) {
      setLoadingCategories(false);
      setCategories([]);
      return;
    }
    let cancelled = false;
    setLoadingCategories(true);
    searchCategories({ isActive: true, pageSize: 500, withAttributes: false })
      .then((res) => {
        if (!cancelled) setCategories(res?.items || (Array.isArray(res) ? res : []));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error loading categories:", err);
          toast.error("Failed to load material categories");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCategories(false);
      });
    return () => { cancelled = true; };
  }, [readOnly]);

  const getCategoryId = (cat) =>
    cat?.material_category_id ?? cat?.materialCategoryId ?? cat?.id ?? "";
  const getCategoryName = (cat) =>
    cat?.category_name ?? cat?.categoryName ?? cat?.name ?? "";

  const addNewRow = () => {
    setNewItems((prev) => [...prev, { materialCategoryId: "", details: "" }]);
  };

  const removeNewRow = (index) => {
    setNewItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateNewItem = (index, field, value) => {
    setNewItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSaveRow = async (index) => {
    if (phaseId == null || isNaN(phaseId)) {
      toast.error("Invalid project phase.");
      return;
    }
    const row = newItems[index];
    const catId = parseInt(row.materialCategoryId, 10) || 0;
    if (!catId) {
      toast.error("Select a material category.");
      return;
    }
    setSavingRowIndex(index);
    try {
      await axiosInstance.post("/interior-designers/developer-project-boq", {
        projectPhaseId: phaseId,
        items: [
          { materialCategoryId: catId, details: String(row.details ?? "").trim() },
        ],
      });
      toast.success("BOQ item created successfully.");
      setNewItems((prev) => prev.filter((_, i) => i !== index));
      fetchList();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to create BOQ item.";
      toast.error(msg);
    } finally {
      setSavingRowIndex(null);
    }
  };

  const handleCancelRow = (index) => {
    removeNewRow(index);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setEditCategoryId(
      String(item.materialCategoryId ?? item.material_category_id ?? "")
    );
    setEditDetails(item.details ?? "");
    setOpenEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    const boqId =
      editingItem.developerProjectBoqId ?? editingItem.developer_project_boq_id;
    if (!boqId) return;
    const catId = parseInt(editCategoryId, 10);
    if (!catId) {
      toast.error("Select a material category.");
      return;
    }
    setUpdating(true);
    try {
      await axiosInstance.put(
        `/interior-designers/developer-project-boq/${boqId}`,
        { materialCategoryId: catId, details: editDetails.trim() }
      );
      toast.success("BOQ item updated successfully.");
      setOpenEditModal(false);
      setEditingItem(null);
      fetchList();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to update BOQ item.";
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingItem(null);
  };

  const handleOpenDeleteModal = (boqId) => {
    setBoqToDeleteId(boqId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setBoqToDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (boqToDeleteId == null) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(
        `/interior-designers/developer-project-boq/${boqToDeleteId}`
      );
      toast.success("BOQ item deleted successfully.");
      setList((prev) =>
        prev.filter(
          (item) =>
            (item.developerProjectBoqId ?? item.developer_project_boq_id) !==
            boqToDeleteId
        )
      );
      handleCloseDeleteModal();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to delete BOQ item.";
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={className || "bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"}>
      {!readOnly && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleCloseDeleteModal}
          message="Are you sure you want to delete this BOQ item?"
          isLoading={isDeleting}
          loadingText="Deleting..."
        />
      )}

      {showTitle && title && (
        <h2 className="text-base font-semibold text-slate-900 px-4 pt-4 mb-3">
          {title}
        </h2>
      )}

      {loading ? (
        <div className="overflow-x-auto">
          <table className="min-w-full min-h-full divide-y divide-gray-200">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  Material Category
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  Details
                </th>
                {!readOnly && (
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="h-6 bg-gray-200 rounded animate-pulse w-40"></div>
                  </td>
                  {!readOnly && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-8 bg-gray-200 rounded animate-pulse w-24"></div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full min-h-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  {!readOnly && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {list.map((item) => {
                  const boqId =
                    item.developerProjectBoqId ?? item.developer_project_boq_id;
                  const categoryName =
                    item.materialCategoryName ?? item.material_category_name ?? "—";
                  const categoryCode =
                    item.materialCategoryCode ?? item.material_category_code ?? "";
                  const details = item.details ?? "";
                  return (
                    <tr
                      key={boqId}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {categoryName}
                        {categoryCode ? ` (${categoryCode})` : ""}
                      </td>
                      <td className={`px-6 py-4 text-sm text-gray-600 ${readOnly ? "max-w-prose" : "max-w-xs truncate"}`}>
                        {readOnly ? (
                          <span className="whitespace-pre-wrap break-words">{details || "—"}</span>
                        ) : (
                          details || "—"
                        )}
                      </td>
                      {!readOnly && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex justify-start space-x-2">
                            <EditButton
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleEditClick(item);
                              }}
                            />
                            <DeleteButton
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleOpenDeleteModal(boqId);
                              }}
                              disabled={isDeleting}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {readOnly && list.length === 0 && (
                  <tr>
                    <td
                      colSpan={2}
                      className="px-6 py-10 text-center text-sm text-gray-500"
                    >
                      No developer BOQ entries for this phase yet.
                    </td>
                  </tr>
                )}
                {!readOnly && newItems.map((row, index) => (
                  <tr
                    key={`new-${index}`}
                    className="hover:bg-gray-50 transition-colors duration-150 bg-gray-50/50"
                  >
                    <td className="px-4 py-2 align-middle">
                      <select
                        value={row.materialCategoryId}
                        onChange={(e) =>
                          updateNewItem(index, "materialCategoryId", e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full min-w-[200px] rounded border border-gray-300 px-3 py-2 text-sm"
                        disabled={loadingCategories}
                      >
                        <option value="">Select category</option>
                        {categories.map((cat) => (
                          <option
                            key={getCategoryId(cat)}
                            value={getCategoryId(cat)}
                          >
                            {getCategoryName(cat)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-2 align-middle">
                      <input
                        type="text"
                        value={row.details}
                        onChange={(e) =>
                          updateNewItem(index, "details", e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder="Details"
                        className="w-full min-w-[180px] rounded border border-gray-300 px-3 py-2 text-sm"
                      />
                    </td>
                    <td className="px-6 py-2 text-right align-middle">
                      <div className="flex justify-start space-x-2">
                        <PrimaryButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSaveRow(index);
                          }}
                          disabled={
                            loadingCategories || savingRowIndex !== null
                          }
                          className="px-4 py-1.5 text-sm"
                        >
                          {savingRowIndex === index ? "Saving…" : "Save"}
                        </PrimaryButton>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleCancelRow(index);
                          }}
                          disabled={savingRowIndex !== null}
                          className="px-4 py-1.5 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                onClick={addNewRow}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm hover:bg-gray-100"
              >
                + Add row
              </button>
            </div>
          )}
        </>
      )}

      {error && (
        <p className="text-red-500 text-center py-4">{error}</p>
      )}

      {!readOnly && openEditModal && editingItem && (
        <div
          className="fixed inset-0 bg-gray-800 bg-opacity-40 flex items-center justify-center z-50"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit BOQ Item</h2>
              <CloseButton onClick={handleCloseEditModal} />
            </div>
            <form
              onSubmit={handleEditSubmit}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Material Category
                </label>
                <select
                  value={editCategoryId}
                  onChange={(e) => setEditCategoryId(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  required
                  disabled={loadingCategories}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option
                      key={getCategoryId(cat)}
                      value={getCategoryId(cat)}
                    >
                      {getCategoryName(cat)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-6">
                <CustomInput
                  type="text"
                  label="Details"
                  placeholder="Details"
                  value={editDetails}
                  onChange={(e) => setEditDetails(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex justify-end">
                <PrimaryButton
                  type="submit"
                  disabled={updating}
                  className="px-6 py-2"
                >
                  {updating ? "Updating…" : "Update"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperProjectBoqTable;
